import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, FlexStyle, Platform,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import { Area, Floor, Space } from '../../../../../components/Map/SetFloorsModal/FloorItem';
import isMobile from '../../../../../components/IsMobile';

interface AreaDropdownProps {
  selectedArea: Area,
  setAreaDropdownShown: Dispatch<SetStateAction<boolean>>,
  setSelectedArea: Dispatch<SetStateAction<Area>>,
  setSelectedSpace: Dispatch<SetStateAction<Space>>,
  setSelectedLocationId?: Dispatch<SetStateAction<string>>,
  floor: Floor,
  areaDropdownShown: boolean,
  closeOtherDropdowns: () => void,
  hideLabel?: boolean,
  listPositionAbsolute?: boolean,
  style?: FlexStyle | FlexStyle[],
}

const AreaDropdown = ({
  setAreaDropdownShown, setSelectedLocationId, setSelectedArea, setSelectedSpace, floor, hideLabel,
  selectedArea, areaDropdownShown, closeOtherDropdowns, listPositionAbsolute, style,
}: AreaDropdownProps) => {
  const [value, onChangeText] = useState<string>('');

  return <View
    style={[styles.container, style]}
    testID='filter-assets-area-list'
  >
    <View style={{ flexDirection: 'row' }}>
      <View style={styles.connectorContainer}>
        <View style={styles.floorAreaConnector} />
        <View style={styles.connectorSpot} />
      </View>
      <View style={{ flex: 1 }}>
        {hideLabel ? <View style={{ marginBottom: styles.heading.marginBottom }} />
          : <Text style={styles.heading}>{'Area'}</Text>}
        {areaDropdownShown ? <View style={styles.dropdownBox}>
          <TextInput
            placeholder='Search'
            style={[{ marginStart: 8 }, !isMobile && { outlineWidth: 0 }]}
            placeholderTextColor={Colors.light.hereworksPrimaryGrey700}
            {...{ value, onChangeText }}
          />
          <TouchableOpacity
            style={{ flex: 1, alignItems: 'flex-end' }}
            onPress={() => {
              setAreaDropdownShown(false);
              closeOtherDropdowns();
            }}
          >
            <FontAwesome
              size={20}
              style={styles.icon}
              name={'angle-up'}
            />
          </TouchableOpacity>
        </View> : <TouchableOpacity
          style={styles.dropdownBox}
          onPress={() => {
            setAreaDropdownShown(!areaDropdownShown);
            closeOtherDropdowns();
          }}
          testID='filters-location-select-area-btn'
        >
          <Text style={[
            styles.dropdownBoxText,
            !selectedArea && { color: Colors.light.hereworksPrimaryGrey800 },
          ]}>
            {selectedArea?.Name ?? 'None'}
          </Text>
          <View style={{ alignItems: 'flex-end' }}>
            <FontAwesome
              size={20}
              style={styles.icon}
              name={areaDropdownShown ? 'angle-up' : 'angle-down'}
            />
          </View>
        </TouchableOpacity>}
      </View>
    </View>
    <View style={styles.dropdownContainer}>
      {areaDropdownShown && <ScrollView
        nestedScrollEnabled
        indicatorStyle={'black'}
        style={[styles.dropdownView, listPositionAbsolute && styles.absoluteView]}
        testID='filters-areas-list'
      >
        {[null, ...Object.values(floor?.areas ?? {}) ?? []].map((area: Area) => {
          const selected: boolean = area?.LocationId === selectedArea?.LocationId;
          return (!(value?.length > 0)
            || area?.Name.toLowerCase().includes(value?.trim().toLowerCase())) && <TouchableOpacity
              key={`area_option_${area?.LocationId}`}
              onPress={() => {
                setSelectedArea(area);
                setSelectedLocationId?.(area?.LocationId ?? floor?.LocationId);
                if (!selected) setSelectedSpace(null);
                setAreaDropdownShown(false);
                closeOtherDropdowns();
              }}
              style={selected && styles.itemSelected}
            >
              <Text style={[styles.dropdownItemText, selected && styles.textSelected]}>
                {area?.Name ?? 'ALL AREAS'}
              </Text>
            </TouchableOpacity>;
        })}
      </ScrollView>}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginStart: 16,
  },
  dropdownBox: {
    width: '100%',
    minHeight: 40,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  heading: {
    fontSize: 14,
    lineHeight: 18,
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  dropdownContainer: {
    marginStart: 20,
  },
  dropdownView: {
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
    marginTop: 4,
    maxHeight: 180,
    alignSelf: 'stretch',
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  textSelected: {
    color: '#FFF',
  },
  itemSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  icon: {
    color: Colors.light.hereworksBlue500,
    marginRight: 12,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  floorAreaConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderBottomWidth: 1,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  absoluteView: Platform.OS === 'android' ? {} : {
    position: 'absolute',
    width: '100%',
  },
});

export default AreaDropdown;
